/**
 * Deep linking utilities for Capacitor native app
 * Handles detection of native app context and generation of deep link URLs
 */

/**
 * Detect if the request is coming from a native mobile app
 * Checks User-Agent header for Capacitor/mobile app indicators
 */
export function isNativeAppRequest(request?: Request): boolean {
  if (!request) return false;
  
  const userAgent = request.headers.get("user-agent") || "";
  const referer = request.headers.get("referer") || "";
  
  // Check for Capacitor/mobile app indicators in User-Agent
  const isCapacitorUA = 
    /Capacitor/i.test(userAgent) ||
    /Cordova/i.test(userAgent) ||
    /Ionic/i.test(userAgent) ||
    /WebView/i.test(userAgent);
  
  // Check for mobile app origin in referer
  const isMobileOrigin = 
    referer.includes("capacitor://") ||
    referer.includes("file://") ||
    referer.includes("ionic://");
  
  // Check for custom header that mobile app can send
  const isMobileHeader = request.headers.get("x-mobile-app") === "true";
  
  return isCapacitorUA || isMobileOrigin || isMobileHeader;
}

/**
 * Get deep link URL for native app
 * Returns a URL scheme that the native app can intercept
 */
export function getDeepLinkUrl(path: string, params?: Record<string, string>): string {
  const queryString = buildQueryString(params);
  
  // Use growthsync:// scheme for deep linking
  // The path should start with / (e.g., /checkout/success)
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  
  return `growthsync://app${cleanPath}${queryString}`;
}

/**
 * Get return URL for Stripe checkout
 * For native apps: Use custom URL scheme (growthsync://) that opens the app directly
 * For web: Use web URLs (https://) as Stripe requires
 * 
 * Note: Stripe Checkout supports custom URL schemes in success_url and cancel_url
 * The deep link will be opened by the in-app browser (SFSafariViewController/Chrome Custom Tabs)
 * which will then open the native app via the URL scheme.
 */
export function getStripeReturnUrl(
  baseUrl: string,
  path: string,
  params?: Record<string, string>,
  isNativeApp?: boolean
): string {
  if (isNativeApp) {
    // For native apps, use custom URL scheme that the app can intercept
    // Format: growthsync://app/checkout/success?session_id=xxx
    const queryString = buildQueryString(params);
    
    const cleanPath = path.startsWith("/") ? path : `/${path}`;
    return `growthsync://app${cleanPath}${queryString}`;
  }
  
  // For web, use normal web URLs
  const queryString = buildQueryString(params);
  
  return `${baseUrl}${path}${queryString}`;
}

function buildQueryString(params?: Record<string, string>): string {
  if (!params || Object.keys(params).length === 0) {
    return "";
  }

  const encoded = new URLSearchParams(params).toString();

  // Stripe replaces this exact token in success_url.
  // URLSearchParams percent-encodes braces, so restore the literal token.
  const withStripePlaceholder = encoded.replace(
    /%7BCHECKOUT_SESSION_ID%7D/gi,
    "{CHECKOUT_SESSION_ID}"
  );

  return `?${withStripePlaceholder}`;
}

/**
 * Client-side: Detect if running in Capacitor native app
 */
export function isNativeAppClient(): boolean {
  if (typeof window === "undefined") return false;
  
  const protocol = window.location.protocol;
  const origin = window.location.origin;
  const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : "";
  
  return (
    protocol === "capacitor:" ||
    protocol === "file:" ||
    protocol === "ionic:" ||
    origin.includes("capacitor://") ||
    origin.includes("file://") ||
    origin.includes("ionic://") ||
    /Capacitor|Cordova|Ionic|WebView/i.test(userAgent)
  );
}

/**
 * Normalize a potentially user-provided return path.
 * Allows only internal app paths and falls back safely.
 */
export function normalizeInternalReturnPath(
  returnTo: string | null | undefined,
  fallbackPath: string = "/"
): string {
  const safeFallback = fallbackPath.startsWith("/") ? fallbackPath : `/${fallbackPath}`;
  if (!returnTo) return safeFallback;

  const decoded = (() => {
    try {
      return decodeURIComponent(returnTo);
    } catch {
      return returnTo;
    }
  })().trim();

  // Block external URLs and non-http schemes.
  if (
    decoded.startsWith("http://") ||
    decoded.startsWith("https://") ||
    decoded.startsWith("//") ||
    decoded.startsWith("javascript:") ||
    decoded.startsWith("data:")
  ) {
    return safeFallback;
  }

  // Only allow internal absolute paths.
  if (!decoded.startsWith("/")) {
    return safeFallback;
  }

  return decoded;
}
